import { Navigate, createBrowserRouter } from 'react-router-dom';
import { entrySurfaceForHostname } from './config/site-host-routing';

import { AppShell } from '../components/layout/AppShell';
import { ActivityPage } from '../features/activity/routes/ActivityPage';
import { FreshConversationPage } from '../features/conversations/routes/FreshConversationPage';
import { ConversationPage } from '../features/conversations/routes/ConversationPage';
import { ConversationsPage } from '../features/conversations/routes/ConversationsPage';
import { AddDocumentsPage } from '../features/documents/routes/AddDocumentsPage';
import { DocumentDetailPage } from '../features/documents/routes/DocumentDetailPage';
import { DocumentsPage } from '../features/documents/routes/DocumentsPage';
import { InvestigationDetailPage } from '../features/investigations/routes/InvestigationDetailPage';
import { InvestigationsPage } from '../features/investigations/routes/InvestigationsPage';
import { InvestigationLearningPage } from '../features/investigations/routes/InvestigationLearningPage';
import { MachineMemoryPage } from '../features/machine-memory/routes/MachineMemoryPage';
import { CreateMachinePage } from '../features/machines/routes/CreateMachinePage';
import { MachineEntryPage } from '../features/machines/routes/MachineEntryPage';
import { MachineLandingPage } from '../features/machines/routes/MachineLandingPage';
import { MachinesPage } from '../features/machines/routes/MachinesPage';
import { UsersPage } from '../features/users/routes/UsersPage';
import { ProfilePage } from '../features/profile/routes/ProfilePage';
import { OrganizationPage } from '../features/organization/routes/OrganizationPage';
import { CustomerSelectionPage } from '../auth/CustomerSelectionPage';
import { OrganizationUnavailablePage } from '../auth/OrganizationUnavailablePage';
import { ProtectedRoute } from '../auth/ProtectedRoute';
import { SignInPage } from '../auth/SignInPage';
import { ForgotPasswordPage } from '../auth/ForgotPasswordPage';
import { VerifyEmailPage } from '../auth/VerifyEmailPage';
import { LandingPage } from '../features/marketing/routes/LandingPage';
import { CompanyPage } from '../features/marketing/routes/CompanyPage';
import { HowRoute42WorksPage } from '../features/marketing/routes/HowRoute42WorksPage';
import { DesignPartnersPage } from '../features/marketing/routes/DesignPartnersPage';
import { BillingPage } from '../features/billing/routes/BillingPage';
import { HelpCenterPage } from '../features/product-guidance/routes/HelpCenterPage';
import { HelpArticlePage } from '../features/product-guidance/routes/HelpArticlePage';
import { TourShell } from '../features/product-tour/components/TourShell';
import { TourHomePage } from '../features/product-tour/routes/TourHomePage';
import { TourMachinePage } from '../features/product-tour/routes/TourMachinePage';
import { TourDocumentsPage } from '../features/product-tour/routes/TourDocumentsPage';
import { TourConversationPage } from '../features/product-tour/routes/TourConversationPage';
import { TourInvestigationPage } from '../features/product-tour/routes/TourInvestigationPage';
import { TourMemoryPage } from '../features/product-tour/routes/TourMemoryPage';
import { TourActivityPage } from '../features/product-tour/routes/TourActivityPage';
import { TourSummaryPage } from '../features/product-tour/routes/TourSummaryPage';

export const router = createBrowserRouter([
  { path: '/', element: entrySurfaceForHostname(window.location.hostname) === 'customer' ? <Navigate to="/machines" replace /> : <LandingPage /> },
  { path: '/company', element: <CompanyPage /> },
  { path: '/how-route42-works', element: <HowRoute42WorksPage /> },
  { path: '/design-partners', element: <DesignPartnersPage /> },
  { path: '/sign-in', element: <SignInPage /> },
  { path: '/forgot-password', element: <ForgotPasswordPage /> },
  { path: '/verify-email', element: <VerifyEmailPage /> },
  {
    element: <ProtectedRoute />,
    children: [
      { path: '/organization-unavailable', element: <OrganizationUnavailablePage /> },
      {
        path: '/tour',
        element: <TourShell />,
        children: [
          { index: true, element: <TourHomePage /> },
          { path: 'machine', element: <TourMachinePage /> },
          { path: 'documents', element: <TourDocumentsPage /> },
          { path: 'conversation', element: <TourConversationPage /> },
          { path: 'investigation', element: <TourInvestigationPage /> },
          { path: 'memory', element: <TourMemoryPage /> },
          { path: 'activity', element: <TourActivityPage /> },
          { path: 'summary', element: <TourSummaryPage /> },
          { path: '*', element: <Navigate to="/tour" replace /> },
        ],
      },
      {
        element: <AppShell />,
        children: [
          { path: '/organizations', element: <CustomerSelectionPage /> },
          { path: '/customers', element: <Navigate to="/organizations" replace /> },
          { path: '/start', element: <MachineLandingPage /> },
          { path: '/machines', element: <MachinesPage /> },
          { path: '/machines/new', element: <CreateMachinePage /> },
          { path: '/machines/:machineId', element: <MachineEntryPage /> },
          { path: '/machines/:machineId/conversations', element: <ConversationsPage /> },
          { path: '/machines/:machineId/conversations/new', element: <FreshConversationPage /> },
          { path: '/machines/:machineId/conversations/:conversationId', element: <ConversationPage /> },
          { path: '/machines/:machineId/investigations', element: <InvestigationsPage /> },
          { path: '/machines/:machineId/investigations/:investigationId', element: <InvestigationDetailPage /> },
          { path: '/machines/:machineId/documents', element: <DocumentsPage /> },
          { path: '/machines/:machineId/documents/add', element: <AddDocumentsPage /> },
          { path: '/machines/:machineId/documents/:documentId', element: <DocumentDetailPage /> },
          { path: '/machines/:machineId/memory', element: <MachineMemoryPage /> },
          { path: '/machines/:machineId/activity', element: <ActivityPage /> },
          { path: '/settings/users', element: <UsersPage /> },
          { path: '/settings/profile', element: <ProfilePage /> },
          { path: '/settings/organization', element: <OrganizationPage /> },
          { path: '/settings/billing', element: <BillingPage /> },
          { path: '/learn/investigations', element: <InvestigationLearningPage /> },
          { path: '/help', element: <HelpCenterPage /> },
          { path: '/help/:articleId', element: <HelpArticlePage /> },
        ],
      },
    ],
  },
  { path: '*', element: <Navigate to="/machines" replace /> },
]);
